/**
 * Off-ticket work on a Team queue: what Proctor and Harper did on their own
 * today, folded into one "Also today" line under Done, with the rows inside.
 *
 *   reviews   `GET /api/code-review/desks/:deskId/prs`, the pull requests on
 *             this Team's repositories Proctor reviewed today
 *   posts     `GET /api/workers/harper/engagements/:deskId/runs`, Harper's
 *             runs that posted today
 *
 * Off-ticket work that needs a person is already in Needs you: a review
 * Proctor holds is a review case work item, and the queue prints it there
 * (`ticket/queueGroups.ts`). Harper's runs have no state that waits for a
 * person, so no post joins Needs you.
 *
 * Both lists are read once per Team and cached (`useAlsoToday`): there is no
 * count-only read, and the fold's line and count need them closed or open.
 *
 * A row opens its record in the sheet: `?review=<prStateId>` or
 * `?post=<runId>`, and opening one drops the ticket sheet's `?item=`.
 */
import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Fold, Row } from "../queue/index.js";
import { useOwnedSheet } from "../shell/useOwnedSheet.js";
import { useWorkerLookup } from "../work/useWorkerLookup.js";
import { WorkerMark } from "../workers/WorkerMark.js";
import { CODE_REVIEW_SLUG } from "../workers/crafts.js";
import { agoLabel } from "../ticket/queueGroups.js";
import { reviewsApi, type ReviewsApi } from "../reviews/reviewsApi.js";
import { alsoTodayOf, repoName } from "../reviews/reviewsModel.js";
import { ReviewSheet } from "../reviews/ReviewSheet.js";
import { HARPER_SLUG, PostSheet } from "./PostSheet.js";
import { useAlsoToday } from "./useAlsoToday.js";

export interface OffTicketWorkProps {
  deskId: string;
  teamName: string;
  api?: ReviewsApi;
  now?: number;
}

export function OffTicketWork({ deskId, teamName, api = reviewsApi, now: fixedNow }: OffTicketWorkProps) {
  const workerOf = useWorkerLookup();
  const [params, setParams] = useSearchParams();
  const { prs, runs, reread } = useAlsoToday(api, deskId);

  const now = fixedNow ?? Date.now();
  const also = useMemo(() => alsoTodayOf(prs, runs, now), [prs, runs, now]);
  const order = useMemo(
    () => [
      ...also.reviews.map((pr) => ({ key: "review", id: pr.prStateId })),
      ...also.posts.map((run) => ({ key: "post", id: run.id })),
    ],
    [also],
  );

  const reviewId = params.get("review");
  const postId = params.get("post");
  const openRecord = useCallback(
    (key: "review" | "post", id: string, replace = false) =>
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("item");
          next.delete("add");
          next.delete("settings");
          next.delete("sees");
          next.delete(key === "review" ? "post" : "review");
          next.set(key, id);
          return next;
        },
        replace ? { replace: true } : undefined,
      ),
    [setParams],
  );
  const close = useCallback(
    () =>
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("review");
          next.delete("post");
          return next;
        },
        { replace: true },
      ),
    [setParams],
  );
  const nextAfter = (id: string) => {
    const at = order.findIndex((entry) => entry.id === id);
    const next = at >= 0 ? order[at + 1] : undefined;
    return next ? () => openRecord(next.key as "review" | "post", next.id, true) : null;
  };

  const sheet = reviewId ? (
    <ReviewSheet
      key={reviewId}
      api={api}
      prStateId={reviewId}
      deskId={deskId}
      deskName={teamName}
      unowned={false}
      onChanged={reread}
      onNextReview={nextAfter(reviewId)}
      onClose={close}
    />
  ) : postId ? (
    <PostSheet key={postId} api={api} runId={postId} teamName={teamName} onNext={nextAfter(postId)} onClose={close} />
  ) : null;
  useOwnedSheet(sheet, close);

  const count = also.reviews.length + also.posts.length;
  if (count === 0) return null;

  const proctor = workerOf(CODE_REVIEW_SLUG);
  const harper = workerOf(HARPER_SLUG);
  const medallion = (worker: typeof proctor) =>
    worker ? <WorkerMark avatar={worker.avatar} name={worker.displayName} slug={worker.slug} size="sm" /> : <span aria-hidden />;

  return (
    <Fold
      className="also"
      title={
        <>
          <span className="t">Also today</span>
          <span className="line" data-testid="also-today-line">
            {also.line}
          </span>
        </>
      }
      count={count}
    >
      <div className="rows" data-testid="also-today-rows">
        {also.reviews.map((pr) => (
          <Row
            key={pr.prStateId}
            glyph={medallion(proctor)}
            rowKey={`#${pr.prNumber}`}
            title={pr.title ?? `Pull request #${pr.prNumber}`}
            tag={{ label: pr.githubStatus === "merged" || pr.githubStatus === "closed" ? "Closed" : "Posted" }}
            state={`${repoName(pr.repoFullName)} · ${pr.author ?? "unknown author"} · ${pr.followUp ? "follow-up round" : "round 1"}`}
            when={agoLabel(pr.lastReviewedAt, now)}
            verb="Open"
            sel={reviewId === pr.prStateId}
            onOpen={() => openRecord("review", pr.prStateId)}
          />
        ))}
        {also.posts.map((run) => (
          <Row
            key={run.id}
            glyph={medallion(harper)}
            rowKey="POST"
            title={run.jobName}
            tag={{ label: "Posted", color: "var(--agent-comms)" }}
            state="Posted by Harper"
            when={agoLabel(run.deliveredAt ?? run.createdAt, now)}
            verb="Open"
            sel={postId === run.id}
            onOpen={() => openRecord("post", run.id)}
          />
        ))}
      </div>
    </Fold>
  );
}
